'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { FeedbackDialog } from '@/components/Dialog/FeedbackDialog';
import { Spacer, VStack } from '@/components/Layout';
import { FullScreenOverlay } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { Separator } from '@/components/ui/separator';
import { LeaveConfirmStep } from './-components/LeaveConfirmStep';
import { LeaveDetailFormStep } from './-components/LeaveDetailFormStep';
import { LeaveTypeSelectStep } from './-components/LeaveTypeSelectStep';
import { LEAVE_TYPES } from './-constants';
import type { LeaveRequestDraft, LeaveRequestStep, LeaveTypeId } from './-types';
import { computeLeaveDaysFromDraft } from './-utils/computeLeaveDays';
export const Route = createFileRoute('/_individual/individual/portal/leave-request/')({
  component: LeaveRequestPage
});
const INITIAL_DRAFT: LeaveRequestDraft = {
  typeId: undefined,
  unit: 'fullDay',
  dateRange: undefined,
  singleDate: undefined,
  startTime: '',
  endTime: '',
  reason: ''
};
function LeaveRequestPage() {
  const navigate = Route.useNavigate();
  const [step, setStep] = useState<LeaveRequestStep>('select');
  const [draft, setDraft] = useState<LeaveRequestDraft>(INITIAL_DRAFT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const days = computeLeaveDaysFromDraft(draft);
  const selectedType = LEAVE_TYPES.find(type => type.id === draft.typeId);
  const updateDraft = (patch: Partial<LeaveRequestDraft>) => {
    setDraft(prev => ({
      ...prev,
      ...patch
    }));
  };
  const handleSelectType = (typeId: LeaveTypeId) => {
    updateDraft({
      typeId
    });
  };
  const goToRequests = () => {
    navigate({
      to: '/individual/portal/requests'
    });
  };
  const handleBack = () => {
    if (step === 'select') {
      goToRequests();
    } else if (step === 'form') {
      setStep('select');
    } else {
      setStep('form');
    }
  };
  const handlePrimary = () => {
    if (step === 'select') {
      setStep('form');
    } else if (step === 'form') {
      setStep('confirm');
    } else {
      setIsSubmitting(true);
      // モック: 送信処理を擬似的に遅延させてからローディングを閉じ、完了ダイアログを表示する。
      window.setTimeout(() => {
        setIsSubmitting(false);
        setIsComplete(true);
      }, 700);
    }
  };
  const {
    subtitle,
    primaryLabel,
    backLabel,
    primaryDisabled
  } = stepConfig(step, selectedType?.name, Boolean(draft.typeId), days);
  return <VStack className="mx-auto min-h-full w-full max-w-dialog-lg items-start gap-3 bg-neutral-fill p-6">
      <ActionButton icon="ChevronLeft" variant="mutedGhost" size="sm" className="ml-[-8px]" onClick={handleBack}>
        {backLabel}
      </ActionButton>
      <VStack className="gap-2">
        <Text variant="h2">休暇申請</Text>
        <Text variant="labelLarge" className="text-neutral-muted-on-fill">
          {subtitle}
        </Text>
      </VStack>
      <div className="w-full py-3">
        <Separator />
      </div>
      <Spacer variant="s" />

      {step === 'select' && <LeaveTypeSelectStep selectedTypeId={draft.typeId} onSelect={handleSelectType} />}
      {step === 'form' && <LeaveDetailFormStep draft={draft} hasBalance={selectedType?.hasBalance ?? false} days={days} onChange={updateDraft} />}
      {step === 'confirm' && <LeaveConfirmStep draft={draft} days={days} />}

      <Spacer variant="s" />
      <ActionButton onClick={handlePrimary} className="w-full" disabled={primaryDisabled}>
        {primaryLabel}
      </ActionButton>

      {isSubmitting && <FullScreenOverlay />}
      <FeedbackDialog isOpen={isComplete} iconName="CircleCheckBigIcon" title="休暇申請を提出しました" description="申請先の承認をお待ちください。" buttonLabel="ポータルへ戻る" onClickAction={goToRequests} />
    </VStack>;
}
function stepConfig(step: LeaveRequestStep, selectedTypeName: string | undefined, hasType: boolean, days: number): {
  subtitle: string;
  primaryLabel: string;
  backLabel: string;
  primaryDisabled: boolean;
} {
  switch (step) {
    case 'select':
      return {
        subtitle: '取得する休暇の種別を選択してください。',
        primaryLabel: '次へ',
        backLabel: '申請・届出に戻る',
        primaryDisabled: !hasType
      };
    case 'form':
      return {
        subtitle: selectedTypeName ?? '申請内容を入力してください。',
        primaryLabel: '確認へ進む',
        backLabel: '種別の選択に戻る',
        primaryDisabled: days <= 0
      };
    case 'confirm':
      return {
        subtitle: '申請内容をご確認ください。',
        primaryLabel: 'この内容で申請する',
        backLabel: '入力内容の修正',
        primaryDisabled: false
      };
  }
}